"use client";

import { useEffect, useRef, useState } from "react";
import { site } from "../current";
import type { MediaItem } from "../types";
import { BTN_GHOST, BTN_PRIMARY, TextField } from "./ui";

// Uploading and choosing images in the admin dashboard (uploads go to the website's admin API: POST /media).

export async function uploadFile(file: File, alt = ""): Promise<{ item?: MediaItem; error?: string }> {
  const form = new FormData();
  form.append("file", file);
  form.append("alt", alt);
  try {
    const data = await site().upload<{ ok: boolean; item?: MediaItem; error?: string }>("/media", form);
    return data.ok && data.item ? { item: data.item } : { error: data.error ?? "Upload failed." };
  } catch {
    return { error: "Upload failed. Check your connection and try again." };
  }
}

/** A modal grid of the media library's images, with upload. Returns the chosen image. */
export function MediaPicker({ media, open, onClose, onPick }: { media: MediaItem[]; open: boolean; onClose: () => void; onPick: (image: { url: string; alt: string }) => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [items, setItems] = useState(media);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError("");
    for (const file of Array.from(files)) {
      const { item, error: message } = await uploadFile(file);
      if (item) setItems((list) => [item, ...list]);
      if (message) setError(message);
    }
    setBusy(false);
  };

  const images = items.filter((m) => m.kind === "image");
  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="m-auto max-h-[90svh] w-[min(56rem,calc(100vw-2rem))] rounded-2xl bg-offwhite p-0 text-midnight shadow-2xl backdrop:bg-ink/60"
    >
      <div className="flex max-h-[90svh] flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-midnight/10 p-4">
          <h2 className="text-xl font-bold">Choose an image</h2>
          <div className="flex gap-2">
            <label className={`${BTN_PRIMARY} cursor-pointer`}>
              {busy ? "Uploading…" : "Upload new"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/avif,image/gif" multiple className="sr-only" onChange={(e) => upload(e.target.files)} disabled={busy} />
            </label>
            <button type="button" onClick={onClose} className={BTN_GHOST}>
              Close
            </button>
          </div>
        </div>
        {error && <p role="alert" className="mx-4 mt-3 rounded-lg bg-red-700/10 px-4 py-3 text-sm font-bold text-red-800">{error}</p>}
        <div className="overflow-y-auto p-4">
          {images.length ? (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {images.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick({ url: m.url, alt: m.alt });
                      onClose();
                    }}
                    className="group block w-full overflow-hidden rounded-xl border border-midnight/10 text-start hover:border-teal focus-visible:outline-2 focus-visible:outline-teal"
                  >
                    <img src={m.url} alt={m.alt || m.name} loading="lazy" className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                    <span className="block truncate px-2 py-1.5 text-xs">{m.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-8 text-center text-midnight/70">No images yet. Upload one to get started.</p>
          )}
        </div>
      </div>
    </dialog>
  );
}

/** An image setting: preview, choose/replace, remove, and its alt text. */
export function ImageField({ label, value, onChange, media, hint }: {
  label: string;
  value: { url: string; alt: string } | null;
  onChange: (value: { url: string; alt: string } | null) => void;
  media: MediaItem[];
  hint?: string;
}) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-bold">{label}</span>
      {value?.url ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <img src={value.url} alt="" className="aspect-[16/10] w-full rounded-xl border border-midnight/10 object-cover sm:w-56" />
          <div className="flex flex-1 flex-col gap-3">
            <TextField label="Alt text *" hint="Describe what the image shows, for people who can't see it." value={value.alt} onChange={(alt) => onChange({ ...value, alt })} max={300} />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setPicking(true)} className={BTN_GHOST}>
                Replace
              </button>
              <button type="button" onClick={() => onChange(null)} className={BTN_GHOST}>
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setPicking(true)} className="grid min-h-28 place-items-center rounded-xl border-2 border-dashed border-midnight/20 p-6 text-midnight/70 hover:border-teal hover:text-teal">
          + Choose or upload an image
        </button>
      )}
      {hint && <p className="text-sm text-midnight/70">{hint}</p>}
      <MediaPicker media={media} open={picking} onClose={() => setPicking(false)} onPick={(image) => onChange(image)} />
    </div>
  );
}

